// src/shared/json-table/url-sync.ts
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { TableState } from "./types";
import { decodeFiltersParam, decodeSortParam, encodeFiltersParam, encodeSortParam, shallowEqual } from "./utils";

type UrlKeys = {
  page: string;
  size: string;
  sort: string;
  q: string;
  filters: string;
  cols: string;
};

const keysFor = (id: string): UrlKeys => ({
  page: `${id}.page`,
  size: `${id}.size`,
  sort: `${id}.sort`,
  q: `${id}.q`,
  filters: `${id}.filters`,
  cols: `${id}.cols`, // comma-separated hidden column ids
});

export const parseFromUrl = (id: string, sp: URLSearchParams, fallback: TableState): TableState => {
  const keys = keysFor(id);
  const pageIndex = Math.max(0, parseInt(sp.get(keys.page) || `${fallback.pageIndex}`, 10) - 1);
  const pageSize = parseInt(sp.get(keys.size) || `${fallback.pageSize}`, 10);
  const sort = decodeSortParam(sp.get(keys.sort));
  const filters = decodeFiltersParam(sp.get(keys.filters));
  const q = sp.get(keys.q) ?? fallback.q;
  const hiddenCols = (sp.get(keys.cols)?.split(",").filter(Boolean)) ?? [];
  const columnVisibility = { ...fallback.columnVisibility };
  hiddenCols.forEach((cid) => { columnVisibility[cid] = false; });
  return { ...fallback, pageIndex, pageSize, sort, filters, q, columnVisibility };
};

export const applyToUrl = (id: string, sp: URLSearchParams, state: TableState): URLSearchParams => {
  const keys = keysFor(id);
  const next = new URLSearchParams(sp.toString());

  next.set(keys.page, String(state.pageIndex + 1));
  next.set(keys.size, String(state.pageSize));
  state.sort.length ? next.set(keys.sort, encodeSortParam(state.sort)) : next.delete(keys.sort);
  state.filters.length ? next.set(keys.filters, encodeFiltersParam(state.filters)) : next.delete(keys.filters);
  state.q ? next.set(keys.q, state.q) : next.delete(keys.q);

  const hidden = Object.entries(state.columnVisibility)
    .filter(([, vis]) => vis === false)
    .map(([id]) => id)
    .join(",");
  hidden ? next.set(keys.cols, hidden) : next.delete(keys.cols);

  return next;
};

export const useUrlSync = (id: string, state: TableState, setState: (next: TableState) => void) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => parseFromUrl(id, searchParams, state), [id, searchParams]);

  // 1) URL -> Store
  useEffect(() => {
    if (!shallowEqual(urlState, state)) setState(urlState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState]);

  // 2) Store -> URL
  useEffect(() => {
    const next = applyToUrl(id, searchParams, state);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, state.pageIndex, state.pageSize, state.sort, state.filters, state.q, state.columnVisibility]);
};